import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import TextField from "@/components/TextField";
import Select from "@/components/Select";
import PageHeading from "@/components/PageHeading/PageHeading";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { useNavigate } from "@/router";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { formatEnumLabel } from "../_utils";
import { useRequireCommissionPlatform } from "../useCommissionPlatformGate";
import { CREATE_COMMISSION_PARTNER_MUTATION } from "./mutations";
import type { CommissionPartnerStatus } from "./types";

interface CreatePartnerFormValues {
  name: string;
  legacyId: string;
  status: CommissionPartnerStatus;
}

const statusOptions: CommissionPartnerStatus[] = ["ACTIVE", "INACTIVE"];

export default function CreateCommissionPartnerPage() {
  usePageTitle("/billing/finance/commission/partners/new", "New Partner");
  useRequireCapability({ domain: DOMAIN.BENEFIT_PROGRAMS, permission: PERMISSION.manageGrpCommission });
  useRequireCommissionPlatform();
  const navigate = useNavigate();
  const [createPartner, { loading }] = useMutation(CREATE_COMMISSION_PARTNER_MUTATION);

  const { control, handleSubmit } = useForm<CreatePartnerFormValues>({
    defaultValues: {
      name: "",
      legacyId: "",
      status: "ACTIVE"
    }
  });

  const statusSelectOptions = useMemo(
    () => statusOptions.map((value) => ({ value, label: formatEnumLabel(value) })),
    []
  );

  const onSubmit = handleSubmit(async (values) => {
    try {
      await createPartner({
        variables: {
          input: {
            name: values.name.trim(),
            legacyId: values.legacyId.trim(),
            status: values.status
          }
        }
      });
      showSnackbar("Partner created.");
      navigate("/billing/finance/commission/partners");
    } catch {
      showErrorSnackbar("Unable to create partner. Try again or contact support.");
    }
  });

  return (
    <FocusedPageLayout
      backLink={{ to: "/billing/finance/commission/partners" }}
      stickyActions
      clusterActions={[
        {
          text: "Create partner",
          onClick: onSubmit,
          loading,
          testId: "create-partner"
        }
      ]}
    >
      <PageHeading title="New Partner" />
      <p className="pb-6 text-body-medium text-sys-brand-on-surface-variant">
        Create a sponsor partner record for commission program campaigns.
      </p>

      <form className="flex max-w-xl flex-col gap-6" onSubmit={onSubmit}>
        <Controller
          name="name"
          control={control}
          rules={{ required: "Partner name is required" }}
          render={({ field, fieldState }) => (
            <TextField
              label="Partner name"
              value={field.value}
              onChange={field.onChange}
              error={!!fieldState.error}
              supportingText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="legacyId"
          control={control}
          rules={{ required: "Legacy sponsor ID is required" }}
          render={({ field, fieldState }) => (
            <TextField
              label="Legacy sponsor ID"
              value={field.value}
              onChange={field.onChange}
              error={!!fieldState.error}
              supportingText={fieldState.error?.message ?? "Matches the sponsor ID used in ingest files."}
            />
          )}
        />

        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <Select label="Status" value={field.value} onSelect={field.onChange} options={statusSelectOptions} />
          )}
        />
      </form>
    </FocusedPageLayout>
  );
}
